import type React from 'react';
import { type ReactNode, useCallback, useState } from 'react';
import { useStore } from 'effector-react';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import { useImmer } from 'use-immer';
import classNames from 'classnames';
import {
  type ECategoryItem,
  type GeneralID,
  type SceneData,
  type SceneType,
  SelectStatus,
} from '@types';
import Modal from '@components/Modal';
import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import { $categoryList, $categoryMap, $sceneMap } from '@stores/dataStores';
import Input from '@components/Input';
import useInput from '@hooks/useInput';
import Title from '@components/Title';
import SceneModalItem from './SceneModalItem';
import ImgLine from './img-line.png';
import ImgEmpty from './img-empty.png';

interface Props {
  title: ReactNode;
  subTitle?: string;
  multiple?: boolean;
  defaultSelected?: Set<GeneralID>;
  allowEmpty?: boolean;
  allowedTypes?: SceneType[];
  disabledIds?: GeneralID[];
  onConfirm: (selected: Set<GeneralID>) => void;
  onRequestClose: () => void;
}

const StyledModal = styled(Modal)`
  width: 1000px;
  height: calc(100% - 60px);
`;
const GroupLine = styled.div`
  position: relative;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -27px;
    width: 2px;
    background-color: #efefef;
  }
`;
const SubGroup = styled(GroupLine)`
  padding-bottom: 30px;
`;
const LineFix = styled(GroupLine)`
  height: 30px;
`;
const SubGroupList = styled.div`
  padding-left: 50px;
  & ${SubGroup}:nth-last-child(1) {
    padding-bottom: 0;
    &:after {
      content: none;
    }
  }
`;
const LineIcon = styled.img.attrs({ src: ImgLine, draggable: false })`
  position: absolute;
  left: -27px;
  width: 16px;
  height: 16px;
`;

type ISceneData = SceneData & { isSelected: boolean };

interface TreeNode extends Omit<ECategoryItem, 'sceneIds' | 'categoryIds'> {
  sceneList?: ISceneData[];
  subCategoryList?: TreeNode[];
  /** 所有子场景id */
  subIdSet: Set<GeneralID>;
  /** 所有子场景id, 去除了禁用和类型不匹配的部分, 用于渲染复选框 */
  availableIdSet: Set<GeneralID>;
}

function SceneModal({
  title,
  subTitle,
  multiple = false,
  defaultSelected = new Set(),
  allowEmpty = false,
  allowedTypes,
  disabledIds = [],
  onConfirm,
  onRequestClose,
}: Props) {
  const cateList = useStore($categoryList);
  const cateMap = useStore($categoryMap);
  const sceneMap = useStore($sceneMap);
  const searchInput = useInput<HTMLInputElement>('');
  const [searchText, setSearchText] = useState('');

  const [selected, setSelected] = useImmer<Set<GeneralID>>(
    () => defaultSelected
  );

  const isAllowedType = useCallback(
    (type: SceneType) => {
      if (allowedTypes === undefined) return true;
      return allowedTypes.includes(type);
    },
    [allowedTypes]
  );

  const createList = (categoryList: number[], parentNodes: TreeNode[] = []) => {
    const result: TreeNode[] = [];
    const rootSubIdSet = new Set<GeneralID>();
    const rootAvailableIdSet = new Set<GeneralID>();

    for (let i = 0; i < categoryList.length; i++) {
      const id = categoryList[i];
      const categoryItem = cateMap.get(id);
      if (categoryItem) {
        const node: TreeNode = {
          id: categoryItem.id,
          name: categoryItem.name,
          subIdSet: new Set<GeneralID>(),
          availableIdSet: new Set<GeneralID>(),
        };

        if (categoryItem.categoryIds.length > 0) {
          const {
            result: subList,
            rootAvailableIdSet: subAvailableIdSet,
            rootSubIdSet: subSubIdSet,
          } = createList(categoryItem.categoryIds, [...parentNodes, node]);
          node.subCategoryList = subList;
          subAvailableIdSet.forEach((_id) => {
            rootAvailableIdSet.add(_id);
          });
          subSubIdSet.forEach((_id) => {
            rootSubIdSet.add(_id);
          });
        }

        if (categoryItem.sceneIds.length > 0) {
          node.sceneList = categoryItem.sceneIds
            .map((sceneId) => {
              const item = sceneMap.get(sceneId);
              if (!item) return null;
              return {
                ...item,
                isSelected: selected.has(sceneId),
              };
            })
            .filter((scene): scene is ISceneData => !!scene);
          node.sceneList = [];
          for (let j = 0; j < categoryItem.sceneIds.length; j++) {
            const sceneId = categoryItem.sceneIds[j];
            const item = sceneMap.get(sceneId);
            if (item?.name.includes(searchText)) {
              node.sceneList.push({
                ...item,
                isSelected: selected.has(sceneId),
              });
              rootSubIdSet.add(sceneId);
              node.subIdSet.add(sceneId);
              parentNodes.forEach((parentNode) =>
                parentNode.subIdSet?.add(sceneId)
              );
              if (isAllowedType(item.type) && !disabledIds.includes(sceneId)) {
                rootAvailableIdSet.add(sceneId);
                node.availableIdSet.add(sceneId);
                parentNodes.forEach((parentNode) =>
                  parentNode.availableIdSet?.add(sceneId)
                );
              }
            }
          }
        }

        result.push(node);
      }
    }

    return { result, rootAvailableIdSet, rootSubIdSet };
  };

  const {
    result: list,
    rootAvailableIdSet,
    rootSubIdSet,
  } = createList(cateList);

  const btnDisabled = !allowEmpty && !selected.size;

  const getSelectedStatus = (idSet: Set<GeneralID>): SelectStatus => {
    if (!idSet.size) return SelectStatus.none;
    if (Array.from(idSet).every((id) => selected.has(id))) {
      return SelectStatus.all;
    }
    if (Array.from(idSet).some((id) => selected.has(id))) {
      return SelectStatus.part;
    }
    return SelectStatus.none;
  };

  const handleCheck = (idSet: Set<GeneralID>) => (status: SelectStatus) => {
    if (status === SelectStatus.all) {
      setSelected((draft) => {
        Array.from(idSet).forEach((id) => {
          draft.delete(id);
        });
      });
    } else {
      setSelected((draft) => {
        Array.from(idSet).forEach((id) => {
          draft.add(id);
        });
      });
    }
  };

  const handleItemClick = useCallback(
    ({ id, isSelected }: { id: GeneralID; isSelected: boolean }) => {
      if (isSelected) {
        setSelected((draft) => {
          draft.delete(id);
        });
      } else {
        setSelected((draft) => {
          if (!multiple) {
            draft.clear();
          }
          draft.add(id);
        });
      }
    },
    [multiple]
  );

  const search = () => {
    const val = searchInput.value.trim();
    if (val) {
      setSearchText(val);
    }
  };

  const clear = () => {
    setSearchText('');
    searchInput.clear();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      search();
    }
  };

  const renderHeader = () => {
    return (
      <div className="flex w-full items-center">
        <Title>{title}</Title>
        <Input
          scheme="light"
          className="ml-10 h-30 w-150"
          disabled={!!searchText}
          placeholder={searchText ? '' : '请输入关键字'}
          onKeyDown={handleKeyDown}
          onClear={searchText ? clear : undefined}
          {...searchInput.bind}
        />
        <Button className="ml-5" onClick={search}>
          搜索
        </Button>
        {!!subTitle && <div className="ml-auto text-orange">{subTitle}</div>}
      </div>
    );
  };

  const renderCheckAll = () => {
    const status = getSelectedStatus(rootAvailableIdSet);
    return (
      <Checkbox
        className="absolute right-0"
        label="选择全部"
        status={status}
        onClick={handleCheck(rootAvailableIdSet)}
      />
    );
  };

  const renderItem = (item: ISceneData) => {
    return (
      <SceneModalItem
        key={item.id}
        data={item}
        disabled={!isAllowedType(item.type) || disabledIds.includes(item.id)}
        isSelected={item.isSelected}
        onClick={handleItemClick}
      />
    );
  };

  const renderSubGroup = (item: TreeNode) => {
    if (!item.subIdSet.size) return null;
    const status = getSelectedStatus(item.availableIdSet);
    return (
      <SubGroup key={item.id}>
        <LineIcon />
        {multiple ? (
          <Checkbox
            label={item.name}
            status={status}
            onClick={handleCheck(item.availableIdSet)}
          />
        ) : (
          <div className="truncate">{item.name}</div>
        )}
        {!!item.sceneList?.length && (
          <div className={classNames('flex flex-wrap', { 'pl-25': multiple })}>
            {item.sceneList.map(renderItem)}
          </div>
        )}
      </SubGroup>
    );
  };

  const renderGroup = (item: TreeNode) => {
    if (!item.subIdSet.size) return null;
    const status = getSelectedStatus(item.availableIdSet);
    return (
      <div key={item.id} className="mb-30">
        <div className="flex h-50 items-center bg-white-light px-20">
          {multiple ? (
            <Checkbox
              label={item.name}
              status={status}
              onClick={handleCheck(item.availableIdSet)}
            />
          ) : (
            <div className="truncate">{item.name}</div>
          )}
        </div>
        {!!item.subCategoryList?.length && (
          <SubGroupList>
            <LineFix />
            {item.subCategoryList.map(renderSubGroup)}
          </SubGroupList>
        )}
        {!!item.sceneList?.length && (
          <div className="pl-50">
            <div className="mt-10 flex flex-wrap">
              {item.sceneList.map(renderItem)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <StyledModal
      isOpen
      header={renderHeader()}
      footer={
        <>
          <Button
            disabled={btnDisabled}
            onClick={() => {
              onConfirm(selected);
            }}
          >
            确认操作
          </Button>
          {multiple && rootSubIdSet.size > 0 && renderCheckAll()}
        </>
      }
      onRequestClose={onRequestClose}
    >
      {rootSubIdSet.size ? (
        <Scrollbars>{list.map(renderGroup)}</Scrollbars>
      ) : (
        <div className="flex h-full flex-col items-center justify-center">
          <img src={ImgEmpty} className="h-100 w-100" alt="" />
          暂无搜索结果
        </div>
      )}
    </StyledModal>
  );
}

export default SceneModal;
